import React, { useMemo, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { Search, RefreshCw, Download } from "lucide-react";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import * as XLSX from "xlsx";
import { useCurrentDomain } from "../../context/DomainContext";
import { useCompanies } from "../../hooks";
import { getWeeklyPaymentReport } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import { THEME } from "../../theme/themeColors";
import WeeklyPaymentDetailsReport from "./WeeklyPaymentDetailsReport";
import "./WeeklyPaymentReport.css";

const predefinedBottomRanges = [
  { label: "Today", value: [new Date(), new Date()] },
  { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
  { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
  { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
  { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
  {
    label: "Last month",
    value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))],
  },
  {
    label: "This year",
    value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)],
  },
  {
    label: "Last year",
    value: [
      new Date(new Date().getFullYear() - 1, 0, 1),
      new Date(new Date().getFullYear() - 1, 11, 31),
    ],
  },
];

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const toYmd = (d) => {
  const dt = new Date(d);
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${m}-${day}`;
};

const money = (v) => (v == null || v === "" ? "" : Number(v).toFixed(2));

const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

const CompanyOption = (props) => {
  const { data, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        cursor: "pointer",
        background: isSelected ? THEME.primary : props.isFocused ? "#f3f4f6" : "#fff",
        color: isSelected ? THEME.onPrimary : "#111827",
      }}
    >
      <input type="checkbox" checked={isSelected} readOnly style={{ margin: 0 }} />
      <span style={{ fontSize: 13 }}>{data.label}</span>
    </div>
  );
};

const WeeklyPaymentReport = () => {
  const { domain } = useCurrentDomain();
  const companies = useCompanies(domain);

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [detailsCtx, setDetailsCtx] = useState(null);

  const [viewMode, setViewMode] = useState("summary");
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(25);
  const [detailSearch, setDetailSearch] = useState("");
  const [appliedDetailSearch, setAppliedDetailSearch] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);

  const companyOptions = useMemo(() => {
    const list = Array.isArray(companies) ? companies : companies?.data || companies?.companies || [];
    return list
      .map((c) => ({
        value: c.szCompanyId || c.companyId || c.value || c.SZ_COMPANY_ID,
        label: `${c.szCompanyName || c.companyName || c.label || c.SZ_COMPANY_NAME || ""} (${
          c.szCompanyId || c.companyId || c.SZ_COMPANY_ID || ""
        })`,
      }))
      .filter((c) => c.value);
  }, [companies]);

  const companyIdsParam = (selectedCompanies || [])
    .map((c) => c?.value)
    .filter(Boolean)
    .join(",");

  const basePayload = () => ({
    domain,
    companyIds: companyIdsParam,
    fromDate: toYmd(dateRange[0]),
    toDate: toYmd(dateRange[1]),
  });

  const loadSummary = async () => {
    if (!domain) return toast.error("Domain is missing");
    if (!dateRange || dateRange.length !== 2) return toast.error("Date range is required");

    setLoading(true);
    setSummary([]);
    try {
      const data = await getWeeklyPaymentReport({
        ...basePayload(),
        tabName: "Summary",
        officeId: "",
        pageNumber: 1,
        pageSize: 25,
        isExport: false,
      });
      const body = data?.summary ? data : data?.data || data;
      setSummary(body.summary || []);
      if (!(body.summary || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load report");
      setSummary([]);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (row) => {
    if (!dateRange) return toast.error("Search first");
    if (!row?.paymentCount) return;
    setDetailsCtx({
      domain,
      companyId: row.companyId || "",
      companyLabel: row.account || "",
      officeId: row.officeId || "",
      provider: row.provider || "",
      fromDate: toYmd(dateRange[0]),
      toDate: toYmd(dateRange[1]),
    });
  };

  const handleReset = () => {
    setSelectedCompanies([]);
    setDateRange(null);
    setSummary([]);
    setViewMode("summary");
    setDetailRows([]);
    setDetailTotal(0);
    setDetailPage(1);
    setDetailSearch("");
    setAppliedDetailSearch("");
  };

  const handleDownload = async () => {
    if (!dateRange || dateRange.length !== 2) return toast.error("Date range is required");
    setLoading(true);
    try {
      const data = await getWeeklyPaymentReport({
        ...basePayload(),
        tabName: "Summary",
        officeId: "",
        pageNumber: 1,
        pageSize: 25,
        isExport: true,
      });
      const body = data?.summary ? data : data?.data || data;
      const list = body.summary || [];
      if (!list.length) return toast.error("No rows to export");
      const mapped = list.map((r) => ({
        Account: r.account,
        CompanyId: r.companyId,
        Provider: r.provider,
        OfficeId: r.officeId,
        Payments: r.paymentCount,
        Billed: r.billed,
        "Check Amount": r.checkAmount,
        Interest: r.interest,
        "Period Paid": r.periodPaid,
        "Bill Paid": r.billPaid,
        "Write Off": r.writeOff,
        Balance: r.balance,
      }));
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Summary");
      XLSX.writeFile(wb, "WeeklyPaymentReport_Summary.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const detailTotalPages = Math.max(1, Math.ceil(detailTotal / detailPageSize));
  const safeDetailPage = Math.min(detailPage, detailTotalPages);

  const loadAllDetails = async (page, size, search = appliedDetailSearch) => {
    if (!domain) return toast.error("Domain is missing");
    if (!dateRange || dateRange.length !== 2) return toast.error("Date range is required");

    setDetailLoading(true);
    try {
      const data = await getWeeklyPaymentReport({
        ...basePayload(),
        tabName: "Details",
        officeId: "",
        searchText: search || "",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const body = data?.details ? data : data?.data || data;
      const list = body.details || body.items || [];
      setDetailRows(list);
      setDetailTotal(body.totalCount || 0);
      setDetailPage(page);
      setDetailPageSize(size);
      setAppliedDetailSearch(search || "");
      if (!list.length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setDetailRows([]);
      setDetailTotal(0);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailSearch = () => loadAllDetails(1, detailPageSize, detailSearch.trim());

  const mapDetailRow = (r) => ({
    "Case Number": r.caseNumber,
    "Patient First Name": r.patientFirstName,
    "Patient Last Name": r.patientLastName,
    "Case Type": r.caseType,
    "Bill Number": r.billNumber,
    "Bill Date": formatDate(r.billDate),
    "Visit Date": r.visitDate,
    "Check Date": formatDate(r.checkDate),
    "Check Number": r.checkNumber,
    "Payment Posted Date": formatDate(r.paymentPostedDate),
    Doctor: r.doctor,
    Provider: r.provider,
    OfficeId: r.officeId,
    Location: r.location,
    Billed: r.billed,
    "Check Amount": r.checkAmount,
    Interest: r.interest,
    "Bill Paid": r.billPaid,
    "Write Off": r.writeOff,
    Balance: r.balance,
    Insurance: r.insurance,
    Specialty: r.specialty,
    "Bill Status": r.billStatus,
  });

  const handleDetailDownload = async () => {
    if (!dateRange || dateRange.length !== 2) return toast.error("Date range is required");
    setDetailLoading(true);
    try {
      const data = await getWeeklyPaymentReport({
        ...basePayload(),
        tabName: "Details",
        officeId: "",
        searchText: appliedDetailSearch || "",
        pageNumber: 1,
        pageSize: 25,
        isExport: true,
      });
      const body = data?.details ? data : data?.data || data;
      const list = body.details || body.items || [];
      if (!list.length) return toast.error("No data to download");
      if ((body.totalCount || 0) > list.length) {
        toast.error(`Export incomplete: ${list.length} of ${body.totalCount}`);
      }
      const mapped = list.map(mapDetailRow);
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, "WeeklyPaymentReport_AllDetails.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setDetailLoading(false);
    }
  };

  if (detailsCtx) {
    return (
      <WeeklyPaymentDetailsReport
        domain={detailsCtx.domain}
        companyId={detailsCtx.companyId}
        companyLabel={detailsCtx.companyLabel}
        officeId={detailsCtx.officeId}
        provider={detailsCtx.provider}
        fromDate={detailsCtx.fromDate}
        toDate={detailsCtx.toDate}
        onBack={() => setDetailsCtx(null)}
      />
    );
  }

  return (
    <div className="wpr-page">
      <Toaster position="top-right" />

      <div className="wpr-filters">
        <div className="wpr-field wpr-company">
          <label>Company</label>
          <Select
            options={companyOptions}
            value={selectedCompanies}
            onChange={(opts) => setSelectedCompanies(opts || [])}
            styles={{
              ...selectStyles,
              control: (base, state) => ({
                ...(selectStyles.control ? selectStyles.control(base, state) : base),
                minHeight: 36,
                height: "auto",
                alignSelf: "center",
              }),
              valueContainer: (base) => ({
                ...base,
                flexWrap: "wrap",
                gap: 3,
                paddingTop: 3,
                paddingBottom: 3,
                paddingLeft: 6,
                paddingRight: 6,
              }),
              multiValue: (base) => ({
                ...base,
                margin: 0,
                maxWidth: "100%",
                background: THEME.alpha(0.12),
              }),
              multiValueLabel: (base) => ({
                ...base,
                whiteSpace: "normal",
                fontSize: 11,
                lineHeight: "14px",
                padding: "2px 4px",
              }),
              multiValueRemove: (base) => ({
                ...base,
                paddingLeft: 2,
                paddingRight: 2,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                alignSelf: "center",
                paddingTop: 4,
              }),
            }}
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ Option: CompanyOption }}
            placeholder="All companies"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="wpr-field wpr-date">
          <label>Payment Date *</label>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            ranges={predefinedBottomRanges}
            placeholder="Select date range"
            format="MM/dd/yyyy"
            size="sm"
            cleanable
            placement="bottomStart"
            preventOverflow
          />
        </div>

        <div className="wpr-actions">
          <button
            type="button"
            className="wpr-btn wpr-btn-search"
            onClick={() => {
              if (viewMode === "detailed") {
                loadAllDetails(1, detailPageSize, appliedDetailSearch);
              } else {
                loadSummary();
              }
            }}
            disabled={loading || detailLoading}
          >
            <Search size={16} />
            {loading ? "Loading..." : "Search"}
          </button>
          <button type="button" className="wpr-btn wpr-btn-reset" onClick={handleReset} disabled={loading || detailLoading}>
            <RefreshCw size={16} />
            Reset
          </button>
          {viewMode === "summary" && (
            <button
              type="button"
              className="wpr-btn wpr-btn-reset"
              onClick={handleDownload}
              disabled={loading || !summary.length}
            >
              <Download size={16} />
              Excel
            </button>
          )}
        </div>
      </div>

      <div className="wpr-toggle-row">
        <div className="wpr-toggle">
          <button
            type="button"
            className={`wpr-toggle-btn ${viewMode === "summary" ? "active" : ""}`}
            onClick={() => setViewMode("summary")}
          >
            Summary
          </button>
          <button
            type="button"
            className={`wpr-toggle-btn ${viewMode === "detailed" ? "active" : ""}`}
            onClick={() => {
              setViewMode("detailed");
              if (!dateRange || dateRange.length !== 2) {
                toast.error("Date range is required");
                return;
              }
              loadAllDetails(1, detailPageSize, "");
            }}
          >
            Detailed
          </button>
        </div>

        {viewMode === "detailed" && (
          <div className="wpr-detail-toolbar">
            <input
              className="wpr-input"
              value={detailSearch}
              onChange={(e) => setDetailSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleDetailSearch()}
              placeholder="Case # / Bill # / Check # / Patient"
            />
            <button
              type="button"
              className="wpr-btn wpr-btn-search"
              onClick={handleDetailSearch}
              disabled={detailLoading}
            >
              <Search size={16} />
              Search
            </button>
            <button
              type="button"
              className="wpr-btn wpr-btn-reset"
              onClick={() => {
                setDetailSearch("");
                loadAllDetails(1, detailPageSize, "");
              }}
              disabled={detailLoading}
            >
              <RefreshCw size={16} />
              Reset
            </button>
            <button
              type="button"
              className="wpr-btn wpr-btn-reset"
              onClick={handleDetailDownload}
              disabled={detailLoading || !detailRows.length}
            >
              <Download size={16} />
              Excel
            </button>
          </div>
        )}
      </div>

      {viewMode === "summary" && (
        <div className="wpr-table-wrap">
          {loading && (
            <div className="wpr-loading">
              <span className="wpr-spinner" />
              Loading...
            </div>
          )}
          <table className="wpr-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Provider</th>
                <th>Payments</th>
                <th>Billed</th>
                <th>Check Amount</th>
                <th>Interest</th>
                <th>Period Paid</th>
                <th>Bill Paid</th>
                <th>Write Off</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr>
                  <td colSpan={10} className="wpr-empty">
                    {loading ? "" : "Select date range and click Search"}
                  </td>
                </tr>
              ) : (
                summary.map((r) => (
                  <tr key={`${r.companyId}|${r.officeId || r.provider}`}>
                    <td>{r.account}</td>
                    <td>
                      {r.paymentCount > 0 ? (
                        <button type="button" className="wpr-link" onClick={() => openDetails(r)}>
                          {r.provider}
                        </button>
                      ) : (
                        r.provider
                      )}
                    </td>
                    <td>
                      {r.paymentCount > 0 ? (
                        <button type="button" className="wpr-link" onClick={() => openDetails(r)}>
                          {r.paymentCount}
                        </button>
                      ) : (
                        r.paymentCount
                      )}
                    </td>
                    <td>{money(r.billed)}</td>
                    <td>{money(r.checkAmount)}</td>
                    <td>{money(r.interest)}</td>
                    <td>{money(r.periodPaid)}</td>
                    <td>{money(r.billPaid)}</td>
                    <td>{money(r.writeOff)}</td>
                    <td>{money(r.balance)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "detailed" && (
        <>
          <div className="wpr-table-wrap">
            {detailLoading && (
              <div className="wpr-loading">
                <span className="wpr-spinner" />
                Loading...
              </div>
            )}
            <table className="wpr-table">
              <thead>
                <tr>
                  <th>Case Number</th>
                  <th>Patient</th>
                  <th>Case Type</th>
                  <th>Bill Number</th>
                  <th>Bill Date</th>
                  <th>Visit Date</th>
                  <th>Check Number</th>
                  <th>Check Date</th>
                  <th>Payment Posted Date</th>
                  <th>Doctor</th>
                  <th>Provider</th>
                  <th>Location</th>
                  <th>Billed</th>
                  <th>Check Amount</th>
                  <th>Interest</th>
                  <th>Bill Paid</th>
                  <th>Write Off</th>
                  <th>Balance</th>
                  <th>Insurance</th>
                  <th>Specialty</th>
                  <th>Bill Status</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.length === 0 ? (
                  <tr>
                    <td colSpan={21} className="wpr-empty">
                      {detailLoading ? "" : "Select date range and switch to Detailed"}
                    </td>
                  </tr>
                ) : (
                  detailRows.map((r, i) => (
                    <tr key={`${r.billNumber}|${r.checkNumber}|${i}`}>
                      <td>{r.caseNumber}</td>
                      <td>{`${r.patientFirstName || ""} ${r.patientLastName || ""}`.trim()}</td>
                      <td>{r.caseType}</td>
                      <td>{r.billNumber}</td>
                      <td>{formatDate(r.billDate)}</td>
                      <td>{r.visitDate}</td>
                      <td>{r.checkNumber}</td>
                      <td>{formatDate(r.checkDate)}</td>
                      <td>{formatDate(r.paymentPostedDate)}</td>
                      <td>{r.doctor}</td>
                      <td>{r.provider}</td>
                      <td className="wpr-wrap">{r.location}</td>
                      <td>{money(r.billed)}</td>
                      <td>{money(r.checkAmount)}</td>
                      <td>{money(r.interest)}</td>
                      <td>{money(r.billPaid)}</td>
                      <td>{money(r.writeOff)}</td>
                      <td>{money(r.balance)}</td>
                      <td>{r.insurance}</td>
                      <td>{r.specialty}</td>
                      <td>{r.billStatus}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {detailTotal > 0 && (
            <div className="wpr-pager">
              <Select
                options={pageSizeOptions}
                value={pageSizeOptions.find((o) => o.value === detailPageSize)}
                onChange={(opt) => loadAllDetails(1, opt.value, appliedDetailSearch)}
                styles={selectStyles}
                menuPlacement="top"
                isSearchable={false}
              />
              <span>
                {(safeDetailPage - 1) * detailPageSize + 1}-
                {Math.min(safeDetailPage * detailPageSize, detailTotal)} of {detailTotal}
              </span>
              <button
                type="button"
                className="wpr-btn wpr-btn-reset"
                disabled={safeDetailPage <= 1 || detailLoading}
                onClick={() => loadAllDetails(safeDetailPage - 1, detailPageSize, appliedDetailSearch)}
              >
                Prev
              </button>
              <button
                type="button"
                className="wpr-btn wpr-btn-reset"
                disabled={safeDetailPage >= detailTotalPages || detailLoading}
                onClick={() => loadAllDetails(safeDetailPage + 1, detailPageSize, appliedDetailSearch)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WeeklyPaymentReport;